import { NavLink } from 'react-router-dom';
import { CutTimer } from './cutTimer/CutTimer';
import { useCutTimer } from '../store/cutTimer';
import { useT } from '../lib/i18n';

// 5-slot bottom nav matching the demo's design:
//   - Center "Cut Timer" is an action button — circle is lifted above the bar with
//     a 3px bg-color border for a clean cutout. Gradient flips gold→red (idle) to
//     green→dark-green (running) with a pulsing shadow.
//   - The four navigation tabs use icons + labels. Active tab tints gold-400.
//
// Demo CSS: see .bottom-tab-cut, .bottom-tab-cut-circle, .bottom-tab-cut.is-running
// in DEMO_REFERENCE.html. We mirror those tokens in Tailwind below.
type NavTab = { to: string; labelKey: string; icon: 'home' | 'chart' | 'book' | 'users' };
const navTabs: readonly NavTab[] = [
  { to: '/today', labelKey: 'nav.today', icon: 'home' },
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: 'chart' },
  { to: '/lessons', labelKey: 'nav.lessons', icon: 'book' },
  { to: '/clients', labelKey: 'nav.clients', icon: 'users' },
];

export function BottomTabs() {
  const cutTimerMode = useCutTimer((s) => s.mode);
  const cutType = useCutTimer((s) => s.cutType);
  const openChair = useCutTimer((s) => s.openChair);
  const t = useT();

  // Cut "in progress" includes minimized cuts (mode === 'idle' with persisted cutType).
  // Drives the green pulsing state on the center circle.
  const cutInProgress = !!cutType && cutTimerMode !== 'summary';

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-surface pb-safe">
        <ul className="grid grid-cols-5 max-w-page mx-auto px-1 pt-1">
          {/* Slots 1-2: Today + Dashboard */}
          {navTabs.slice(0, 2).map((t) => (
            <NavTabItem key={t.to} tab={t} />
          ))}

          {/* Slot 3: Cut Timer center button */}
          <li>
            <button
              type="button"
              onClick={openChair}
              aria-label={cutInProgress ? t('nav.cut.resume') : t('nav.cut.start')}
              className="group flex flex-col items-center justify-center w-full h-16 gap-1 px-1.5 active:translate-y-px transition-transform"
            >
              <span
                className={`relative w-[50px] h-[50px] rounded-full grid place-items-center -mt-[22px] border-[3px] border-bg transition-all ${
                  cutInProgress
                    ? 'bg-gradient-to-br from-green-600 to-[#0e5b34] cut-tab-running'
                    : 'bg-gradient-to-br from-gold-500 to-red-600 group-hover:-translate-y-0.5'
                }`}
                style={{
                  boxShadow: cutInProgress
                    ? '0 8px 18px rgba(21,122,71,.36)'
                    : '0 8px 18px rgba(200,16,46,.28), 0 3px 8px rgba(212,169,66,.22)',
                }}
              >
                <ScissorsIcon className="w-[22px] h-[22px] text-white" />
              </span>
              <span
                className={`text-[10px] font-bold tracking-[0.04em] ${
                  cutInProgress ? 'text-green-600' : 'text-ink-500'
                }`}
              >
                {t('nav.cut')}
              </span>
            </button>
          </li>

          {/* Slots 4-5: Lessons + Clients */}
          {navTabs.slice(2).map((t) => (
            <NavTabItem key={t.to} tab={t} />
          ))}
        </ul>
      </nav>

      <CutTimer />

      {/* Pulsing shadow animation for the running state — keyframes can't live in
          the Tailwind class directly, so we inject them once via a style tag. */}
      <style>{`
        @keyframes cutTabRunning {
          0%, 100% { box-shadow: 0 8px 18px rgba(21,122,71,.36); }
          50%      { box-shadow: 0 8px 28px rgba(21,122,71,.6); }
        }
        .cut-tab-running { animation: cutTabRunning 1.6s ease-in-out infinite; }
      `}</style>
    </>
  );
}

function NavTabItem({ tab }: { tab: NavTab }) {
  const t = useT();
  return (
    <li>
      <NavLink
        to={tab.to}
        className={({ isActive }) =>
          `flex flex-col items-center justify-center h-16 text-[10px] font-bold gap-1 transition-colors ${
            isActive
              ? 'text-gold-400 dark:text-gold-400'
              : 'text-ink-500 hover:text-ink-700'
          }`
        }
      >
        <NavIcon name={tab.icon} className="w-[22px] h-[22px]" />
        {t(tab.labelKey)}
      </NavLink>
    </li>
  );
}

function NavIcon({
  name,
  className,
}: {
  name: 'home' | 'chart' | 'book' | 'users';
  className?: string;
}) {
  const props = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (name) {
    case 'home':
      return (
        <svg {...props}>
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4a1 1 0 0 1-1-1v-6h-4v6a1 1 0 0 1-1 1H5a2 2 0 0 1-2-2z" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...props}>
          <line x1="12" y1="20" x2="12" y2="10" />
          <line x1="18" y1="20" x2="18" y2="4" />
          <line x1="6" y1="20" x2="6" y2="16" />
        </svg>
      );
    case 'book':
      return (
        <svg {...props}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
  }
}

function ScissorsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <line x1="20" y1="4" x2="8.12" y2="15.88" />
      <line x1="14.47" y1="14.48" x2="20" y2="20" />
      <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
  );
}
